import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import { firmaApi, VerificarIdentidadData } from '../services/api.ts';

const steps = [
  'Datos Personales',
  'Adjuntar Documentos',
  'Selfie con Prueba de Vida',
  'Verificación SMS',
  'Completado'
];

const VerificarIdentidad: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<VerificarIdentidadData>({
    nombre: '',
    email: '',
    documento: '',
    telefono: '',
    tipoDocumento: 'DNI',
    cuit: '',
    documentoPersoneria: '',
    selfieUrl: '',
    documentoUrl: '',
  });

  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [showRedirectDialog, setShowRedirectDialog] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string>('');
  
  // Estados para los pasos
  const [isEmpresa, setIsEmpresa] = useState(false);
  const [documentoFile, setDocumentoFile] = useState<File | null>(null);
  const [personeriaFile, setPersoneriaFile] = useState<File | null>(null);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = e.target.files?.[0];
    if (file) {
      switch (type) {
        case 'documento':
          setDocumentoFile(file);
          break;
        case 'personeria':
          setPersoneriaFile(file);
          break;
        case 'selfie':
          setSelfieFile(file);
          break;
      }
    }
  };

  const handleSendOTP = async () => {
    setLoading(true);
    try {
      // Simular envío de OTP
      await new Promise(resolve => setTimeout(resolve, 1000));
      setOtpSent(true);
      setError('');
    } catch (err) {
      setError('Error al enviar código OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    try {
      // Simular verificación de OTP
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (otpCode === '123456') { // Código de prueba
        setOtpVerified(true);
        setError('');
        handleNext();
      } else {
        setError('Código OTP incorrecto');
      }
    } catch (err) {
      setError('Error al verificar código OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResultado(null);

    try {
      const response = await firmaApi.verificarIdentidad(formData);
      setResultado(response);
      
      // Si hay una URL de redirección, mostrar el diálogo
      if (response.redirectUrl) {
        setRedirectUrl(response.redirectUrl);
        setShowRedirectDialog(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al verificar identidad');
    } finally {
      setLoading(false);
    }
  };

  const handleRedirect = () => {
    window.open(redirectUrl, '_blank', 'width=800,height=600');
    setShowRedirectDialog(false);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Datos Personales
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isEmpresa}
                      onChange={(e) => setIsEmpresa(e.target.checked)}
                    />
                  }
                  label="Soy una empresa"
                />
              </Grid>
              
              {isEmpresa && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="CUIT (obligatorio para empresas)"
                    name="cuit"
                    value={formData.cuit || ''}
                    onChange={handleInputChange}
                    required={isEmpresa}
                    placeholder="20-12345678-9"
                    helperText="Formato: XX-XXXXXXXX-X"
                  />
                </Grid>
              )}
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={isEmpresa ? "Razón Social" : "Nombre completo"}
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Número de documento"
                  name="documento"
                  value={formData.documento}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de documento</InputLabel>
                  <Select
                    name="tipoDocumento"
                    value={formData.tipoDocumento}
                    onChange={handleInputChange}
                    label="Tipo de documento"
                  >
                    <MenuItem value="DNI">DNI</MenuItem>
                    <MenuItem value="PASSPORT">Pasaporte</MenuItem>
                    <MenuItem value="DRIVERS_LICENSE">Licencia de Conducir</MenuItem>
                    {isEmpresa && <MenuItem value="CUIT">CUIT</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              {isEmpresa && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    📄 Documento de Personería Jurídica (Obligatorio para empresas)
                  </Typography>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const base64 = event.target?.result as string;
                          setFormData(prev => ({
                            ...prev,
                            documentoPersoneria: base64.split(',')[1] // Remover el prefijo data:application/pdf;base64,
                          }));
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    style={{ marginBottom: '16px' }}
                  />
                  <Typography variant="caption" display="block" color="textSecondary">
                    Sube el documento que acredite la personería jurídica de la empresa (PDF, JPG, PNG)
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              📎 Adjuntar Documentos
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Documento de Identidad *
                </Typography>
                <input
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  id="documento-file"
                  type="file"
                  onChange={(e) => handleFileChange(e, 'documento')}
                />
                <label htmlFor="documento-file">
                  <Button variant="outlined" component="span" fullWidth>
                    Seleccionar {formData.tipoDocumento}
                  </Button>
                </label>
                {documentoFile && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Archivo seleccionado: {documentoFile.name}
                  </Typography>
                )}
              </Grid>

              {isEmpresa && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Personería Jurídica *
                  </Typography>
                  <input
                    accept="image/*,.pdf"
                    style={{ display: 'none' }}
                    id="personeria-file"
                    type="file"
                    onChange={(e) => handleFileChange(e, 'personeria')}
                  />
                  <label htmlFor="personeria-file">
                    <Button variant="outlined" component="span" fullWidth>
                      Seleccionar Personería
                    </Button>
                  </label>
                  {personeriaFile && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Archivo seleccionado: {personeriaFile.name}
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              🤳 Selfie con Prueba de Vida
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Para verificar que eres una persona real, necesitamos que te tomes una selfie.
                El sistema detectará automáticamente si eres una persona real.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="selfie-file"
                  type="file"
                  capture="user"
                  onChange={(e) => handleFileChange(e, 'selfie')}
                />
                <label htmlFor="selfie-file">
                  <Button variant="outlined" component="span" fullWidth size="large">
                    📸 Tomar Selfie
                  </Button>
                </label>
                {selfieFile && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Selfie capturada: {selfieFile.name}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              📲 Verificación por SMS
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Enviaremos un código de verificación al número: {formData.telefono}
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSendOTP}
                  disabled={loading || otpSent}
                >
                  {loading ? <CircularProgress size={24} /> : 'Enviar Código OTP'}
                </Button>
              </Grid>

              {otpSent && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Código OTP"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    placeholder="Ingresa el código de 6 dígitos"
                    inputProps={{ maxLength: 6 }}
                  />
                </Grid>
              )}

              {otpSent && (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleVerifyOTP}
                    disabled={loading || !otpCode || otpCode.length !== 6}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Verificar Código'}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              ✅ Verificación Completada
            </Typography>
            
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                ¡Felicidades! Tu identidad ha sido verificada exitosamente.
                Ahora puedes proceder con la firma del documento.
              </Typography>
            </Alert>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Resumen de Verificación
                </Typography>
                <Typography><strong>Nombre:</strong> {formData.nombre}</Typography>
                <Typography><strong>Email:</strong> {formData.email}</Typography>
                <Typography><strong>Documento:</strong> {formData.documento}</Typography>
                <Typography><strong>Teléfono:</strong> {formData.telefono}</Typography>
                <Typography><strong>Estado:</strong> Verificado ✅</Typography>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Verificar Identidad
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
          Para continuar con el proceso de firma, necesitamos verificar tu identidad de forma segura.
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Divider sx={{ mb: 3 }} />

        {renderStepContent(activeStep)}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Atrás
          </Button>
          
          <Box>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Completar Verificación'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  (activeStep === 0 && (!formData.nombre || !formData.email || !formData.documento || !formData.telefono)) ||
                  (activeStep === 1 && !documentoFile) ||
                  (activeStep === 1 && isEmpresa && !personeriaFile) ||
                  (activeStep === 2 && !selfieFile) ||
                  (activeStep === 3 && !otpVerified)
                }
              >
                Siguiente
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Diálogo de redirección */}
      <Dialog open={showRedirectDialog} onClose={() => setShowRedirectDialog(false)}>
        <DialogTitle>Verificación Completada</DialogTitle>
        <DialogContent>
          <Typography>
            ¡Excelente! Tu identidad ha sido verificada. Ahora serás redirigido a Adobe Sign para completar la firma electrónica del documento.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            El proceso de firma es seguro y legalmente válido.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRedirectDialog(false)}>
            Cancelar
          </Button>
          <Button onClick={handleRedirect} variant="contained">
            Ir a Adobe Sign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VerificarIdentidad; 